import React from "react";
import { motion } from "framer-motion";

interface Category {
  id: string;
  icon: string;
  title: string;
  desc: string;
}

interface CategoriesProps {
  categories: Category[];
}

const iconMap: Record<string, React.ReactNode> = {
  users: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="8" cy="10" r="3" fill="#a78bfa" fillOpacity="0.3" stroke="#a78bfa" strokeWidth="1.5"/>
      <circle cx="16" cy="10" r="3" fill="#a78bfa" fillOpacity="0.3" stroke="#a78bfa" strokeWidth="1.5"/>
      <rect x="4" y="16" width="16" height="4" rx="2" fill="#a78bfa" fillOpacity="0.3" stroke="#a78bfa" strokeWidth="1.5"/>
    </svg>
  ),
  zap: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="#f59e0b" fillOpacity="0.3" stroke="#f59e0b" strokeWidth="1.5"/>
    </svg>
  ),
  flag: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" fill="#3b82f6" fillOpacity="0.3" stroke="#3b82f6" strokeWidth="1.5"/>
      <path d="M8 8h8v4H8z" fill="#3b82f6" fillOpacity="0.7"/>
    </svg>
  ),
  brain: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <ellipse cx="12" cy="12" rx="8" ry="6" fill="#ec4899" fillOpacity="0.3" stroke="#ec4899" strokeWidth="1.5"/>
      <ellipse cx="12" cy="12" rx="3" ry="2" fill="#ec4899" fillOpacity="0.7"/>
    </svg>
  ),
  book: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="14" rx="2" fill="#10b981" fillOpacity="0.3" stroke="#10b981" strokeWidth="1.5"/>
      <path d="M8 7v10" stroke="#10b981" strokeWidth="1.5"/>
      <path d="M16 7v10" stroke="#10b981" strokeWidth="1.5"/>
    </svg>
  ),
  party: (
    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20l16-16M4 20c2-6 6-10 12-12M4 20c6-2 10-6 12-12" stroke="#f43f5e" strokeWidth="1.5"/>
      <circle cx="20" cy="4" r="2" fill="#f43f5e" fillOpacity="0.7"/>
    </svg>
  )
};

const Categories: React.FC<CategoriesProps> = ({ categories }) => (
  <section data-section="categories" className="py-12 md:py-16 bg-gradient-to-b from-[#0a0a23]/50 to-transparent">
    <div className="max-w-6xl mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          🎮 Categories
        </h2>
        <p className="text-center text-white/70 mb-8 text-lg">Find the perfect experience for your adventure!</p>
      </motion.div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
        {categories.map((cat, index) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="flex flex-col items-center bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-5 md:p-6 shadow-lg min-h-[160px] md:min-h-[180px] border border-white/10 hover:border-purple-400/50 transition-all cursor-pointer group"
          >
            <motion.div
              className="mb-3"
              whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
              transition={{ duration: 0.5 }}
              aria-hidden="true"
            >
              {iconMap[cat.icon]}
            </motion.div>
            <div className="font-semibold text-base md:text-lg mb-2 text-white text-center group-hover:text-purple-300 transition-colors" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {cat.title}
            </div>
            <div className="text-xs md:text-sm text-white/70 text-center leading-relaxed">
              {cat.desc}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Categories;
